import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Params, RouterModule } from '@angular/router';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, forkJoin, of, combineLatest } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { CrmModelDrivenFormComponent } from '../model-driven-form/form/model-driven-form.component';
import {
  CrmInjectionToken,
  CrmDependencies,
  FieldInterface,
  ObjectType,
  FieldConfigInterface,
  SavingStatus,
  LoadingStatus,
  FormDefaultInputValue,
} from '../../tokens-and-interfaces';
import { CRMTrackingService } from '../../shared-services/page-analytics.service';
import { PAGE_ROUTES, SNACKBAR_DURATION } from '../../constants';
import { TranslationModule } from '../../i18n/translation-module';
import { PreviewObjectService } from '../../shared-services/preview-object.service';
import { TranslateByObjectTypePipe } from '../../i18n/translate-by-object-type/translate-by-object-type.pipe';
import { TranslateByObjectTypeService } from '../../i18n/translate-by-object-type/translate-by-object-type.service';
import { CrmFormService } from '../../shared-services/crm-services/crm-form.service';

@Component({
  selector: 'crm-contact-modal',
  templateUrl: './create-modal.component.html',

  imports: [
    CommonModule,
    GalaxyPageModule,
    MatButtonModule,
    MatIconModule,
    RouterModule,
    TranslationModule,
    TranslateByObjectTypePipe,
    CrmModelDrivenFormComponent,
    MatDialogModule,
  ],
  standalone: true,
})
export class CreateContactModalComponent implements OnInit {
  @Input({ required: true }) objectType!: ObjectType;
  LoadingStatus = LoadingStatus;
  SavingStatus = SavingStatus;
  loadingStatus$$ = new BehaviorSubject<LoadingStatus>(LoadingStatus.LOADING);
  savingStatus$$ = new BehaviorSubject<SavingStatus>(SavingStatus.NOT_SAVING);
  routePrefix$ = this.config.routePrefix$;
  fieldConfig$: Observable<FieldConfigInterface> = of(<FieldConfigInterface>{});
  PAGE_ROUTES = PAGE_ROUTES;

  crmObjectId$ = this.route.params.pipe(
    map((params: Params) => params['crmObjectId']),
    shareReplay(1),
  );

  constructor(
    private dialogRef: MatDialogRef<CreateContactModalComponent>,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly crmFormService: CrmFormService,
    private readonly snackService: SnackbarService,
    private readonly route: ActivatedRoute,
    private readonly previewObjectService: PreviewObjectService,
    private readonly trackingService: CRMTrackingService,
    private readonly translateByObjectTypeService: TranslateByObjectTypeService,
  ) {}

  ngOnInit(): void {
    const fieldSchemas$ = combineLatest([
      this.crmFormService.listAllFormFields$(this.objectType),
      this.defaultInputValues$(),
    ]).pipe(
      map(([fieldSchemas, defaultInputValues]) => {
        return fieldSchemas.map((fieldSchema) => {
          const defaultInputValue = defaultInputValues.find(
            (inputValue) => inputValue.fieldId === fieldSchema.fieldId && !!inputValue.value,
          );
          if (defaultInputValue) {
            fieldSchema.value = defaultInputValue.value;
            fieldSchema.hasDefaultValue = true;
          }
          return fieldSchema;
        });
      }),
      take(1),
    );
    const fieldGroups$ = this.crmFormService.listAllObjectFormFieldGroups$(this.objectType);

    this.fieldConfig$ = of(null).pipe(
      tap(() => this.loadingStatus$$.next(LoadingStatus.LOADING)),
      switchMap(() => forkJoin([fieldSchemas$, fieldGroups$])),
      tap(([fieldSchemas, fieldGroups]) => {
        const empty = fieldSchemas.length === 0 || fieldGroups.length === 0;
        this.loadingStatus$$.next(empty ? LoadingStatus.EMPTY : LoadingStatus.OK);
      }),
      map(([fieldSchemas, fieldGroups]) => {
        return {
          fieldSchemas: fieldSchemas,
          fieldGroups: fieldGroups,
        };
      }),
      catchError((error) => {
        this.loadingStatus$$.next(LoadingStatus.ERROR);
        this.errorSnackbar(error, 'ERRORS.GENERIC_MESSAGE');
        return of({} as FieldConfigInterface);
      }),
      shareReplay(1),
    );
  }

  onSave(fields: FieldInterface[]): void {
    of(null)
      .pipe(
        take(1),
        tap(() => this.savingStatus$$.next(SavingStatus.SAVING)),
        switchMap(() => this.crmFormService.validateAndCreateCrmObject$(this.objectType, fields)),
        catchError((err) => {
          this.savingStatus$$.next(SavingStatus.ERROR);
          throw err;
        }),
        tap(() => this.savingStatus$$.next(SavingStatus.OK)),
        tap((previewCrmObject) => {
          this.previewObjectService.setPreviewCrmObject(previewCrmObject, this.objectType);
        }),

        tap((previewCrmObject) => this.dialogRef.close(previewCrmObject)),

        switchMap(() => this.config.routePrefix$),
      )
      .subscribe({
        next: () => {
          this.trackingService.trackEvent(this.objectType, 'added-crm-object');
        },
        error: (error) => {
          this.trackingService.trackEvent(this.objectType, 'failed-add-crm-object');
          this.errorSnackbar(error, 'ERRORS.GENERIC_MESSAGE');
        },
      });
  }

  private defaultInputValues$(): Observable<FormDefaultInputValue[]> {
    switch (this.objectType) {
      case 'Contact':
        return this.config.contact?.formDefaultOnCreateInputValues$ ?? of([]);
      case 'Company':
        return this.config.company?.formDefaultOnCreateInputValues$ ?? of([]);
      default:
        return of([]);
    }
  }

  private errorSnackbar(error: any | null, defaultMessage: string) {
    let message: string | null = null;
    if (error && error.message) {
      message = error.message;
    }
    const translatedDefaultMessage = this.translateByObjectTypeService.getTranslationByObjectType(
      this.objectType,
      defaultMessage,
    );
    this.snackService.openErrorSnack(message ?? translatedDefaultMessage, {
      duration: SNACKBAR_DURATION,
    });
  }

  onCancel(): void {
    this.trackingService.trackEvent(this.objectType, 'cancelled-crm-add');
    this.dialogRef.close();
  }
}
